import { formatDateTimeRangeToISO } from "../../../../utils/dateTimeUtils";
import { OrderFiltersType } from "../../../../graphql/types/OrderFiltersType";

/**
 * 把页面筛选状态转成后端 query 变量。
 *
 * 由「包裹查询」与「历史包裹查询」两个 hook 共用——两者的筛选条件结构完全相同
 * （历史页只是少了异常信息那一组），变量构建逻辑没有理由维护两份。
 */
export const buildCentralParcelQueryVariables = (
    filters: OrderFiltersType,
    currentPage: number,
    pageSize: number,
    sortField?: string | null,
    sortDirection?: 'ASC' | 'DESC' | null
) => {
    // receivedAtHub 功能已移除

    const timeRanges = {
        orderTime: formatDateTimeRangeToISO(filters.orderTime),
        receivedAtHub: formatDateTimeRangeToISO(filters.receivedAtHub),
        destHubArrivedAt: formatDateTimeRangeToISO(filters.destHubArrivedAt),
        destWarehouseArrivedAt: formatDateTimeRangeToISO(filters.destWarehouseArrivedAt),
        deliverAt: formatDateTimeRangeToISO(filters.deliverAt),
        markExceptionAt: formatDateTimeRangeToISO(filters.markExceptionAt),
        deliveredAt: formatDateTimeRangeToISO(filters.deliveredAt),
        lastDeliveryScanTime: formatDateTimeRangeToISO(filters.lastDeliveryScanTime),
        updatedAt: formatDateTimeRangeToISO(filters.updatedAt),
        returnArrivalHubAt: formatDateTimeRangeToISO(filters.returnArrivalHubAt)
    };

    // 处理 receiverZipcode：如果有原始输入，转换为数组（与 trackingNumbers 一致）
    let receiverZipcode: string[] | undefined = filters.receiverZipcode;
    if (filters.receiverZipcodeInput) {
        // 支持换行符、半角逗号和空格分隔（多个空格会被视为一个分隔符）
        const zipcodes = filters.receiverZipcodeInput
            .split(/[\r\n,\s]+/)  // 按换行符、逗号或空格分割（\s+ 匹配一个或多个空白字符）
            .map(z => z.trim())
            .filter(z => z.length > 0);
        receiverZipcode = zipcodes.length > 0 ? zipcodes : undefined;
    }
    
    // 构建查询过滤器，避免单值和多值字段冲突
    const queryFilters: any = {
        ...filters,
        ...timeRanges,
        // 转换数组类型为多值查询
        channelIds: filters.channelId?.map(id => parseInt(id)) || undefined,
        pickupHubIds: filters.pickupHubId?.map(id => parseInt(id)) || undefined,
        overallStatuses: filters.overallStatus || undefined,
        nextActions: (filters.nextActions && filters.nextActions.length > 0) ? filters.nextActions : undefined,
        deliveredDspIds: (filters.deliveredDspIds && filters.deliveredDspIds.length > 0) ? filters.deliveredDspIds : undefined,
        receiverZipcode: receiverZipcode, // 使用转换后的数组
        pickupTaskDate: undefined, // 不发送到后端
        // 转换 hubId 和 warehouseId 为数字类型
        hubId: filters.hubId ? parseInt(filters.hubId) : undefined,
        warehouseId: filters.warehouseId ? parseInt(filters.warehouseId) : undefined
    };
    
    // 清空临时输入字段，不发送到后端
    queryFilters.receiverZipcodeInput = undefined;
    
    // 清空单值字段，避免冲突
    queryFilters.channelId = undefined;
    queryFilters.pickupHubId = undefined;
    queryFilters.overallStatus = undefined;
    queryFilters.nextAction = undefined;
    queryFilters.deliveredDspId = undefined;

    // 移除无效的时间范围字段，避免GraphQL schema错误
    Object.keys(queryFilters).forEach(key => {
        const value = queryFilters[key];
        if (value === undefined) {
            delete queryFilters[key];
            return;
        }
        if (typeof value === 'object' && value !== null) {
            const hasStart = Object.prototype.hasOwnProperty.call(value, 'start');
            const hasEnd = Object.prototype.hasOwnProperty.call(value, 'end');
            if ((hasStart || hasEnd) && value.start === undefined && value.end === undefined) {
                delete queryFilters[key];
            }
        }
    });

    return {
        filters: queryFilters,
        pageRequest: {
            page: currentPage,
            size: pageSize,
            sortBy: sortField || "orderTime",
            sortDirection: sortDirection || "DESC"
        }
    };
};
